/**
 * SignIn.jsx - User Sign In Page
 * 
 * @description
 * Authentication page for existing users to sign into their QuickLinks account.
 * Fully integrated with backend authentication API.
 * 
 * @route /signin
 * @auth Not required (public page)
 * @redirects /dashboard on successful login
 * 
 * @formFields
 * - email: User email address (validated)
 * - password: User password
 * - rememberMe: Checkbox for persistent session
 * 
 * @features
 * - Form validation with error messages
 * - Loading states during submission
 * - Toast notifications for feedback
 * - Dark theme support
 * - Mobile responsive design
 * 
 * @validation
 * - Email: Required, valid email format
 * - Password: Required, minimum 6 characters
 * - Real-time validation on blur
 * - Clear errors on field change
 * 
 * @stateManagement
 * - formData: Email and password values
 * - errors: Validation error messages
 * - isLoading: Submission state
 * - showPassword: Password visibility toggle
 * 
 * @authentication
 * - Validates credentials with backend API
 * - Handles JWT token storage
 * - Redirects to dashboard on success
 * - Shows appropriate error messages
 * 
 * @styling
 * - Gradient background (light mode)
 * - Dark theme with custom colors
 * - Animated form transitions
 * - Focus states on inputs
 * 
 * @dependencies
 * - LoadingButton for submit state
 * - Toast for notifications
 * - React Router for navigation
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LoadingButton } from '../components/LoadingSpinner';
import { useToast } from '../components/Toast';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { login, getApiErrorMessage } from '../services/api.js';

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme] = useState(() => localStorage.getItem('theme') || 'light');
  const { showSuccess, showError, ToastContainer } = useToast();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Show success message if redirected from forgot password page
  useEffect(() => {
    if (location.state?.message) {
      showSuccess(location.state.message);
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showSuccess]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Call backend login API
      const res = await login(formData.email, formData.password);
      
      // Verify token was set properly
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('Token not set after login');
        showError('Authentication failed. Please try again.');
        setIsLoading(false);
        return;
      }
      
      console.log('Login successful, token stored');
      showSuccess('Welcome back! Redirecting to dashboard...');
      
      // Store remember me preference if needed
      if (formData.rememberMe) {
        localStorage.setItem('userEmail', formData.email);
      }
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Login error:', error);
      showError(getApiErrorMessage(error));
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? 'themed' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
      <ToastContainer />
      
      <div className="max-w-md w-full space-y-8 animate-fade-in-up">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center">
            <img src="/logo.svg" alt="Link Spaghetti Logo" className="h-10 mb-2" />
          </Link>
          <Link to="/" className={`text-3xl font-bold inline-block ${theme === 'dark' ? 'text-transparent bg-gradient-to-r from-[#2980b9] to-[#16a085] bg-clip-text' : 'bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent'}`}>
            Link Spaghetti
          </Link>
          <h2 className={`mt-6 text-3xl font-extrabold ${theme === 'dark' ? 'themed-text' : 'text-gray-900'}`}>
            Welcome back
          </h2>
          <p className={`mt-2 text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>
            Or{' '}
            <Link to="/signup" className={`font-medium ${theme === 'dark' ? 'text-[#2980b9] hover:text-[#16a085]' : 'text-purple-600 hover:text-purple-500'}`}>
              create a new account
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`appearance-none rounded-none relative block w-full px-3 py-2 ${
                  theme === 'dark' ? 'themed-secondary themed-text themed-border border-gray-700' : 'border border-gray-300 text-gray-900'
                } rounded-t-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm ${
                  errors.email ? 'border-red-500 animate-shake' : ''
                }`}
                placeholder="Email address"
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.email}</p>
              )}
            </div>
            
            <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`appearance-none rounded-none relative block w-full px-3 py-2 ${
                  theme === 'dark' ? 'themed-secondary themed-text themed-border border-gray-700' : 'border border-gray-300 text-gray-900'
                } rounded-b-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm ${
                  errors.password ? 'border-red-500 animate-shake' : ''
                }`}
                placeholder="Password"
                disabled={isLoading}
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.password}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between animate-fade-in" style={{ animationDelay: '300ms' }}>
            <div className="flex items-center">
              <input
                id="remember-me"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                disabled={isLoading}
              />
              <label htmlFor="remember-me" className={`ml-2 block text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-900'}`}>
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <Link to="/forgot-password" className={`font-medium ${theme === 'dark' ? 'text-[#2980b9] hover:text-[#16a085]' : 'text-purple-600 hover:text-purple-500'}`}>
                Forgot your password?
              </Link>
            </div>
          </div>

          <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
            <LoadingButton
              type="submit"
              loading={isLoading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                theme === 'dark' ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085] hover:from-[#3498db] hover:to-[#1abc9c]' : 'button-gradient'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 transform hover:scale-105`}
              disabled={isLoading}
            >
              Sign in
            </LoadingButton>
          </div>

          <div className="mt-6 animate-fade-in" style={{ animationDelay: '500ms' }}>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={`w-full border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${theme === 'dark' ? 'themed-secondary themed-text-secondary' : 'bg-gray-50 text-gray-500'}`}>
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6">
              <GoogleSignInButton 
                theme={theme}
                disabled={isLoading}
                className="animate-fade-in"
                style={{ animationDelay: '600ms' }}
              />
            </div>
          </div>
        </form>
        
        <div className="text-center text-xs text-gray-500 mt-6 animate-fade-in" style={{ animationDelay: '700ms' }}>
          By signing in, you agree to our{' '}
          <Link to="/privacy" className="underline hover:text-gray-700">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms" className="underline hover:text-gray-700">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default SignIn;